import React from 'react';
import { Typography } from 'antd';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { CustomTooltip } from '../../../../components/form/fields/custom-tooltip';
import { C } from '../../constants/design-tokens';

const { Text } = Typography;

export interface CardOption {
  id: string;
  label: string;
  imageUrl?: string;
}

interface CardSelectorProps {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
  options: CardOption[];
  value: string;
  onChange: (id: string) => void;
  extra?: React.ReactNode;
}

export interface CardShellProps {
  border?: string;
  background?: string;
  cursor?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: React.ReactNode;
}

export const CardShell: React.FC<CardShellProps> = ({
  border = '1px solid rgba(0,0,0,0.12)',
  background = 'var(--color-background-primary)',
  cursor = 'pointer',
  onClick,
  onMouseEnter,
  onMouseLeave,
  children
}) => (
  <div
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      width: 90,
      borderRadius: 8,
      overflow: 'hidden',
      cursor,
      border,
      background,
      transition: 'border-color 0.12s, background 0.12s'
    }}
  >
    {children}
  </div>
);

export const CardContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      width: '100%',
      aspectRatio: '1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {children}
  </div>
);

export const CardSelector: React.FC<CardSelectorProps> = ({ label, icon, hint, options, value, onChange, extra }) => {
  const { spacing, fontSize } = useResponsiveSizes();

  return (
    <div style={{ marginBottom: spacing.md }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.sm
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          {icon && (
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: C.surface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.textSecondary,
                fontSize: fontSize.sm
              }}
            >
              {icon}
            </span>
          )}
          <span
            style={{
              fontSize: fontSize.md,
              fontWeight: 600,
              color: C.textPrimary
            }}
          >
            {label}
          </span>
        </div>
        {hint && <span style={{ fontSize: fontSize.sm, color: C.textMuted }}>{hint}</span>}
      </div>
      <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
        {options.map(option => (
          <Card
            key={option.id}
            option={option}
            isSelected={value === option.id}
            onClick={() => onChange(option.id === value ? '' : option.id)}
          />
        ))}
        {extra}
      </div>
    </div>
  );
};

const Card: React.FC<{
  option: CardOption;
  isSelected: boolean;
  onClick: () => void;
}> = ({ option, isSelected, onClick }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <CustomTooltip field={{ tooltipImage: option.imageUrl }}>
      <div>
      <CardShell
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        border={`2px solid ${isSelected ? C.accent : hovered ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.12)'}`}
        background={isSelected ? C.accentSurface : undefined}
      >
        {option.imageUrl ? (
          <img
            src={option.imageUrl}
            alt={option.label}
            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <CardContent>
            <Text type='secondary' style={{ fontSize: 11, fontWeight: 500 }}>
              {option.label.substring(0, 2).toUpperCase()}
            </Text>
          </CardContent>
        )}
        <div style={{ padding: '5px 6px', borderTop: '0.5px solid rgba(0,0,0,0.08)' }}>
          <Text
            style={{
              fontSize: 10,
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: isSelected ? 500 : 400,
              color: isSelected ? C.accent : undefined
            }}
          >
            {option.label}
          </Text>
        </div>
      </CardShell>
      </div>
    </CustomTooltip>
  );
};
